import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, FormControlLabel, Checkbox, IconButton, Tooltip } from "@mui/material";
import { EditNote } from "@mui/icons-material";

import { red, green, grey } from '@mui/material/colors';
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, isNull, isUndefined, size } from "lodash";

import mpReviewService from "@plannel/services/mp/mp-review-service";
import versionNoteService from "@plannel/services/system/version-note-service";
import NotesModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/NotesModal";

import SplitterContainer from "@plannel/components/SplitterContainer";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader"
import Snackbar from "@plannel/components/Snackbar";
import PivotChartComposed from "@plannel/components/rechart/PivotChartComposed";

import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import ZeroExclusionFilter from "@plannel/components/filter/ZeroExclusionFilter";

import "@plannel/scss/components/CustomGrid.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import MpVersionFilter from "@plannel/components/filter/MpVersionFilter";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import ItemTypeFilter, { ITEM_TYPE } from "@plannel/components/filter/ItemTypeFilter";
import DimensAttrModal from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/DimensAttrModal";
import p13nService from "@plannel/services/system/p13n-service";
import companyService from "@plannel/services/data/company-service";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import { FS } from "@plannel/utils/font-util";

const { BOH, DEMAND_QTY, BACKORDER_QTY, PRODUCTION_PLAN, SHIPMENT_QTY, EOH, SUPPLY_QTY } = CodeUtil.mpMeasure;
const { snackBarMessageColor } = ColorUtil;
const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
const { itemType, versionStatusCode, buckets } = CodeUtil;

const { RELEASED, PLANNED } = versionStatusCode;
const measureKeyMap = {
  [SHIPMENT_QTY]: "supplyQty"
};

/* 노트가 달린 셀 표식 — RP Review 와 같은 규격 */
const NOTE_MARKER = "3px solid #4caf50";

const FrozenCellRenderer = ({ value, valueFormatted, label }) => (
  <Tooltip title={label} placement="bottom-start">
    <Box component="span" sx={{ display: "block", width: "100%" }}>{valueFormatted ?? value}</Box>
  </Tooltip>
);

const MpReview = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [10, 30, 50];
  const resultTotalCount = useRef(null);
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});

  const [mpResults, setMpResults] = useState([]);
  const [isChildComp, setIsChildComp] = useState(reduxViewState?.isChildComp ?? false);

  // item attribute
  const [dimenModalOpen, setDimenModalOpen] = useState(false);
  const [dimensionAttrData, setDimensionAttrData] = useState(null);
  const [attrConfig, setAttrConfig] = useState(null);

  const gridRef = useRef();
  const bucketColumnDefs = useRef([]);
  const [gridLoading, setGridLoading] = useState(false);
  const colsHide = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "colsHide") ? reduxViewState.colsHide : true;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState([]);
  const allMeasure = useMemo(() => [BOH, DEMAND_QTY, BACKORDER_QTY, PRODUCTION_PLAN, SHIPMENT_QTY, EOH], []);
  const rowsPerCombination = useMemo(() => { return measures?.length }, [measures]);
  const [appliedExceptions, setAppliedExceptions] = useState(reduxViewState?.appliedExceptions ?? []);

  const gridHeaderMap = useRef({});
  const chartSelectedRowsRef = useRef([]); // 마지막 차트 선택 행 — 언어 변경 시 재조립용
  const [chartHeaders, setChartHeaders] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartLabel, setChartLabel] = useState([]);

  const chartSeries = [
    { id: DEMAND_QTY, field: DEMAND_QTY, text: t("demandQty"), type: "bar", typeOptions: { fill: ColorUtil.colorSet(0), barSize: 20 }, orientation: "left", groupName: t("qty") },
    { id: PRODUCTION_PLAN, field: PRODUCTION_PLAN, text: t("productionPlan"), type: "bar", typeOptions: { fill: ColorUtil.colorSet(1), barSize: 20 }, orientation: "left", groupName: t("qty") },
    { id: SUPPLY_QTY, field: SUPPLY_QTY, text: t("supplyQty"), type: "bar", typeOptions: { fill: ColorUtil.colorSet(2), barSize: 20 }, orientation: "left", groupName: t("qty") },
    { id: BOH, field: BOH, text: t("boh"), type: "line", typeOptions: { stroke: ColorUtil.colorSet(3), strokeWidth: 1.5 }, orientation: "right", groupName: t("qty") },
    { id: EOH, field: EOH, text: t("eoh"), type: "line", typeOptions: { stroke: ColorUtil.colorSet(4), strokeWidth: 1.5 }, orientation: "right", groupName: t("qty") },
  ];

  const pivotConfig = {
    groupKey: "measure",
    chartSeries,
    include: chartLabel,
    xAxisSortMap: gridHeaderMap.current
  };

  const ZERO_DEMAND = "zeroDemand";
  const ZERO_PROD = "zeroProd";
  const ZERO_EOH = "zeroEoh";

  const EXCEPTION_LIST = [
    ZERO_DEMAND,
    ZERO_PROD,
    ZERO_EOH
  ];

  const [mpVersionFilter, setMpVersionFilter] = useState(reduxViewState?.mpVersion ? reduxViewState.mpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });

  const [itemTypeFilter, setItemTypeFilter] = useState(reduxViewState?.itemType ? reduxViewState.itemType : null);

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  // 노트 — noteOpen 이 true 면 패널이 닫힌 상태다(RP Review 와 같은 규약)
  const [noteOpen, setNoteOpen] = useState(true);
  const [noteCellData, setNoteCellData] = useState({});
  const [updateNoteData, setUpdateNoteData] = useState({});
  const noteKeySet = useRef(new Set());

  const getRowStyle = params => {
    if (params?.data?.measure && isEqual(params.data.measure, BOH)) {
      return { background: green[50] };
    }

    return null;
  }

  const cellStyle = params => {
    const { data, value, colDef } = params;
    const style = {};
    if (data && value) {
      if (data?.measure && isEqual(data.measure, EOH) && value < 0) {
        style.color = red[500];
      }
    }
    if (data?.frozenHeaders?.has(colDef?.field)) {
      style.backgroundColor = grey[300];
    }
    if (data && noteKeySet.current.size) {
      const noteKey = CodeUtil.getNoteKey(viewName, {
        locCd: data.locCd, itemCd: data.itemCd, baseDate: colDef?.field, measure: data.measure
      });
      if (noteKeySet.current.has(noteKey)) {
        style.borderLeft = NOTE_MARKER;
      }
    }
    return isEmpty(style) ? null : style;
  }

  /* 이 버전에 노트가 달린 셀 키. 셀 표식(cellStyle)이 쓴다 */
  const retrieveNoteKeys = () => {
    if (isEmpty(mpVersionFilter)) {
      return;
    }
    versionNoteService.list({ planVersionId: mpVersionFilter.id, moduleCds: ["MP"] })
      .then(response => {
        noteKeySet.current = new Set((response.data?.result ?? []).map(note => note.noteKey));
        gridRef.current?.api?.refreshCells({ force: true });
      })
      .catch(e => debug(e));
  }

  const handleNoteModal = () => {
    setNoteOpen(!noteOpen);
  }

  const emptyValueFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value)) {
      return "";
    }
    return NumberUtil.formatNumberData(params, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  const getAttributeColDefs = (isExcel = false) => {
    const itemColDefs = [];
    const attrDatas = dimensionAttrData?.attr;

    if (!attrDatas) {
      return itemColDefs;
    }

    for (let i = 0; i < attrDatas?.length; i ++) {
      const attrData = attrDatas[i];
      const attrId = `prod_${attrData}`;
      const sort = isEqual(sortParams?.orderByColumn, attrId) ? sortParams?.sortType : null;
      const attrNm = attrConfig?.item ? attrConfig.item[attrData] ?? attrData : attrData;
      const fieldNm = attrData;

      const itemAttrArry = isExcel ? [
        {
          headerName: attrNm,
          colId: attrId,
          field: fieldNm,
          cellClass: "stringType",
          width: 120,
        },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
        }] : [{
          headerName: attrNm,
          colId: attrId,
          showRowGroup: fieldNm,
          chartDataType: "category",
          pinned: "left", lockPinned: true,
          cellRenderer: "agGroupCellRenderer",
          cellClass: "stringType",
          cellRendererParams: {
            suppressCount: true,
          },
          width: 120,
          sortable: true,
          sort
        },
        {
          field: fieldNm,
          editable: false,
          hide: true,
          cellClass: "stringType",
          rowGroup: true,
          sort
        }
      ];
      itemColDefs.push(...itemAttrArry);
    }

    return itemColDefs;
  }

  const itemTypeValueFormatter = params => {
    const { value } = params;

    return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
  };

  const defaultColumnDefs = useMemo(() => {
    const isCategory = !isEmpty(filterValue?.category);
    const attributeColDefs = isCategory ? {} : {
      // column header dimension modal
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        enableMenu: false,
        isSorting: true,
        isClicked: () => {
          setDimenModalOpen(true);
        }
      },
    }
    const itemColDefs = getAttributeColDefs();

    const baseColumns = [
      {
        headerName: 'locCd', colId: "locCdGrp", width: 140, showRowGroup: 'locCd', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { suppressCount: true }
      },
      {
        headerName: 'locName', colId: "locNmGrp", width: 140, showRowGroup: 'locNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { suppressCount: true }
      },
      {
        headerName: isCategory ? 'categoryCd' : 'itemCd', colId: "itemCdGrp", width: 160, showRowGroup: 'itemCd', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellClass: 'stringType',
        cellRendererParams: { suppressCount: true }
        ,...attributeColDefs
      },
      {
        headerName: isCategory ? 'categoryName' : 'itemName', colId: "itemNmGrp", width: 220, showRowGroup: 'itemNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
        headerComponent: CustomDetailHeader,
        headerComponentParams: {
          viewName, colsHide,
          detailColumns: ["itemTypeGrp", "categoryNmGrp"]
        }
      },
      {
        headerName: 'category', colId: "categoryNmGrp", width: 140, showRowGroup: 'categoryNm', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
        headerClass: "header-color-01", hide: colsHide, sortable: false,
      },
      {
        headerName: 'itemType', colId: "itemTypeGrp", width: 140, showRowGroup: 'itemType', pinned: "left", lockPinned: true,
        type: ["nonEditableColumn"], cellRenderer: 'agGroupCellRenderer', cellRendererParams: { suppressCount: true },
        headerClass: "header-color-01", hide: colsHide, sortable: false, valueFormatter: itemTypeValueFormatter
      },
      { headerName: "locCd", field: "locCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "locName", field: "locNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "itemCd", field: "itemCd", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "itemName", field: "itemNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      { headerName: "itemType", field: "itemType", rowGroup: true, hide: true, suppressColumnsToolPanel: true, valueFormatter: itemTypeValueFormatter },
      { headerName: "category", field: "categoryNm", rowGroup: true, hide: true, suppressColumnsToolPanel: true },
      {
        headerName: "measure", field: "measure", width: 200, type: ["nonEditableColumn"], pinned: "left", lockPinned: true,
        valueFormatter: (params) => t(params.value), headerComponent: CustomHeaderMenuIcon,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { suppressCount: true },
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen
        }
      }
    ];

    if (!isEmpty(itemColDefs)) {
      const itemIdx = baseColumns.findIndex(col => isEqual(col.colId, "itemNmGrp"));
      baseColumns.splice(itemIdx + 1, 0, ...itemColDefs);
    }

    return GridUtils.getColumnDefs(baseColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionAttrData, filterValue, sortParams]);

  const defaultExportColumnDefs = useMemo(() => {

    const baseColumns = [
      { field: "locCd", colId: "locCdGrp", rowGroup: true, hide: true },
      { field: "locName", colId: "locNmGrp", rowGroup: true, hide: true },
      { field: "itemCd", colId: "itemCdGrp", rowGroup: true, hide: true },
      { field: "itemName", colId: "itemNmGrp", rowGroup: true, hide: true },
      { field: "category", colId: "categoryNmGrp", rowGroup: true, hide: true },
      { field: "itemType", colId: "itemTypeGrp", rowGroup: true, hide: true },
      { headerName: "locCd", field: "locCd", width: 140, cellClass: 'stringType' },
      { headerName: "locName", field: "locNm", width: 140 },
      { headerName: "itemCd", field: "itemCd", width: 140, cellClass: 'stringType' },
      { headerName: "itemName", field: "itemNm", width: 140 },
      { headerName: "category", field: "categoryNm", width: 140 },
      { headerName: "itemType", field: "itemType", width: 140, valueFormatter: itemTypeValueFormatter },
      { headerName: "measure", field: "measure", width: 160, valueFormatter: (params) => t(params.value) }
    ];

    const itemColDefs = getAttributeColDefs(true);
    if (!isEmpty(itemColDefs)) {
      const itemIdx = baseColumns.findIndex(col => isEqual(col.headerName, "itemType"));
      baseColumns.splice(itemIdx + 1, 0, ...itemColDefs);
    }

    return baseColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionAttrData, attrConfig]);

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (!isEmpty(mpVersionFilter)) {
      params.versionId = mpVersionFilter.id;
      const timeBuckets = mpVersionFilter?.planConfigObj?.TIME_BUCKETS;
      params.timeBuckets = !isEmpty(timeBuckets) ? JSON.stringify(timeBuckets) : null;
    }

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (itemTypeFilter) {
      params.itemType = itemTypeFilter;
    }

    if (filterValue.category) {
      params.itemHrchyCd = filterValue.category.hrchyCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      params.item = itemSearch.current;
    }

   if (!isEmpty(appliedExceptions)) {
      params.appliedExceptions = appliedExceptions;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }

      if (resultTotalCount.current) {
        params.totalCount = resultTotalCount.current;
      }
    }
    params.isChildComp = isChildComp;

    return { ...params, ...sortParams };
  }

  const handleDimenClose = () => {
    setDimenModalOpen(false);
  };

  const setGridHeaders = (headers, headerMap) => {
    /* 이전 Column의 Status를 기억하기 때문에 ColumnDefs의 순서를 따라가지 않음에 따라 UK 설정 */
    const bucketPostFix = mpVersionFilter?.id ? GridUtils.IGNORE_P13N_COLLID + mpVersionFilter.id : GridUtils.IGNORE_P13N_COLLID;
    bucketColumnDefs.current = [];

    headers.forEach((header) => {
      const displayHeader = headerMap[header];
      bucketColumnDefs.current.push({
        headerName: displayHeader, field: header, width: 110, sortable: false,
        colId: header + bucketPostFix,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        cellStyle, valueFormatter: emptyValueFormatter,
        cellClassRules: { greyBackground: params => !!params.data?.frozenHeaders?.has(params.colDef?.field) },
        cellRendererSelector: params => params.data?.frozenHeaders?.has(params.colDef?.field)
          ? { component: FrozenCellRenderer, params: { label: GridUtils.gridValueL10N("frozenRangeSim") } }
          : undefined
      });
    });

    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...defaultColumnDefs, ...bucketColumnDefs.current] });
  }

  const setGridDatas = (headers, results) => {
    const datas = [];
    const bohQtyPivotData = "bohQtyPivotData";
    const demandQtyPivotData = "demandQtyPivotData";
    const backorderQtyPivotData = "backorderQtyPivotData";
    const productionPlanPivotData = "productionPlanPivotData";
    const shipmentQtyPivotData = "shipmentQtyPivotData";
    const eohQtyPivotData = "eohQtyPivotData";

    for (const resultsData of results) {
      const measure = isEmpty(measures) ? allMeasure : measures;

      for (const measureName of measure) {
        const translationKey = measureKeyMap[measureName] || measureName;
        const data = { ...resultsData, measure: translationKey };

        for (let idx = 0, len = headers.length; idx < len; idx++) {
          const header = headers[idx];

          switch (measureName) {
            case BOH:
              data[header] = resultsData[bohQtyPivotData][idx];
              break;

            case DEMAND_QTY:
              data[header] = resultsData[demandQtyPivotData][idx];
              break;

            case BACKORDER_QTY:
              data[header] = resultsData[backorderQtyPivotData][idx];
              break;

            case PRODUCTION_PLAN:
              data[header] = resultsData[productionPlanPivotData][idx];
              if (resultsData.frozenPivotData?.[idx]) {
                data.frozenHeaders = data.frozenHeaders ?? new Set();
                data.frozenHeaders.add(header);
              }
              break;

            case SHIPMENT_QTY:
              data[header] = resultsData[shipmentQtyPivotData][idx];
              break;

            case EOH:
              data[header] = resultsData[eohQtyPivotData][idx];
              break;

            default:
              break;
          }
        }

        datas.push(data);
      }
    }

    return datas;
  }

  const retrieveMpReview = () => {
    gridRef.current?.api?.setGridOption('loading', true);
    const params = getRequestParams();

    mpReviewService.getAll(params)
      .then(response => {
        const { pivotResult, totalPages, totalItems } = response.data;
        const { localDateHeader, headerMap, data } = pivotResult;
        if (pivotResult?.data && !isEmpty(pivotResult.data)) {
          gridHeaderMap.current = headerMap;
          setChartHeaders(localDateHeader);
          setGridHeaders(localDateHeader, headerMap);
          setMpResults(setGridDatas(localDateHeader, data));
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });

          // GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
          setMpResults([]);
        }
        setGridPages(totalPages);
        resultTotalCount.current = totalItems;
      })
      .catch(e => {
        // GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: defaultColumnDefs });
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize, isChildComp, appliedExceptions });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));
      })
      .finally(() => {
        gridRef.current?.api?.setGridOption('loading', false);
      });
  }

  const getCustomConfig = () => {
    companyService.getCustomConfig()
        .then(response => {
          if (response.data) {
            setAttrConfig(response.data);
          } else {
            setAttrConfig({});
          }
        })
        .catch(e => {
          debug(e);
          setAttrConfig({});
        });
  };

  const retrieveAttributeConfig = () => {
    p13nService.get(`${viewName}_ETC`)
        .then(response => {
          if (response.data) {
            const attrConfig = response?.data?.ATTRIBUTE;
            setDimensionAttrData(attrConfig ? response.data.ATTRIBUTE : {});
          }
        })
        .catch(e => {
          debug(e);
          setDimensionAttrData({});
        });
  };

  const onGridReady = params => {
    setGridLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs: defaultColumnDefs, viewName, initState: true });
  }

  const applyMeasures = selectedMeasures => {
    setMeasures(selectedMeasures);
  }

  const setChart = (selectedRows) => {
    const selectedDatas = [];

    chartSelectedRowsRef.current = selectedRows ?? [];

    if (selectedRows && selectedRows.length <= 0) {
      setChartData([]);
      return;
    }

    const headerList = [];
    for (const header of chartHeaders) {
      headerList.push(gridHeaderMap.current[header]);
    }

    for (const row of selectedRows) {
      const chartDatas = {};
      let inputFlag = false;

      chartDatas.measure = t(row.measure);
      for (const header of chartHeaders) {
        if ([BOH, DEMAND_QTY, EOH, PRODUCTION_PLAN, SUPPLY_QTY].includes(row.measure)) {
          const headerName = gridHeaderMap.current[header];
          chartDatas[headerName] = row[header];
          inputFlag = true;
        }
      }

      if (inputFlag) {
        selectedDatas.push(chartDatas);
      }
    }
    setChartLabel(headerList);
    setChartData(selectedDatas);
  }

  /*
   * 언어 변경 — 차트 데이터의 measure 값은 t() 로 번역된 문자열이고, PivotChartComposed 는 그 값을
   * 시리즈의 text 와 맞춰(chartSeries.find(text === dataKey)) 무엇을 그릴지 고른다. 시리즈만 새
   * 언어가 되면 짝이 깨져 그래프가 통째로 사라지므로, 데이터도 같이 다시 만든다(재조회 없음).
   */
  useEffect(() => {
    if (isEmpty(chartSelectedRowsRef.current)) {
      return;
    }
    setChart(chartSelectedRowsRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });

    // 버킷 칸을 누르면 노트 패널의 대상 셀이 된다
    const cellDate = e.colDef?.field;
    const target = e.data;
    if (target && !isEmpty(gridHeaderMap.current[cellDate])) {
      setNoteCellData({
        cellDate,
        // MP 버킷은 일 · 주가 섞인다. 노트 패널의 날짜 표시는 주 형식(yyyy-MM-dd)이 둘 다 담는다
        timeDimension: buckets.WEEK,
        versionDto: { ...mpVersionFilter },
        measure: target.measure,
        itemCd: target.itemCd,
        itemNm: target.itemNm,
        locCd: target.locCd,
        locNm: target.locNm,
        baseDate: cellDate,
      });
    }
  };

  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node.allLeafChildren ? params.node.allLeafChildren : params.node.parent.allLeafChildren
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  }

  const initCheck = (params) => {
    const rowCount = params.api.getDisplayedRowCount();
    if (rowCount === 0) {
      setChartData([]);
    } else {
      const firstNode = params.api.getDisplayedRowAtIndex(0);
      if (firstNode) {
        const selected = firstNode.allLeafChildren ? firstNode.allLeafChildren : firstNode.parent?.allLeafChildren ?? [];
        params.api.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
      }
    }
  }

  const handleCheckbox = e => {
    setIsChildComp(e.target.checked);
  }

  const initPageToRetrieve = () => {
    resultTotalCount.current = null;
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveMpReview();
    }
  }

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getCustomConfig();
    retrieveAttributeConfig();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter) && !isEmpty(measures) && dimensionAttrData !== null && attrConfig !== null) {
      retrieveMpReview();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, measures, sortParams, dimensionAttrData, attrConfig]);

  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter) && !isEmpty(measures)) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter?.id, filterValue, itemTypeFilter, isChildComp, appliedExceptions]);

  useEffect(() => {
    noteKeySet.current = new Set();
    retrieveNoteKeys();
    setNoteCellData({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter?.id]);

  // 노트를 저장 · 삭제하면 그 셀의 표식만 고친다(재조회 없음)
  useEffect(() => {
    if (size(updateNoteData) > 0) {
      const noteKey = CodeUtil.getNoteKey(viewName, updateNoteData);
      if (updateNoteData.deleteFlg) {
        noteKeySet.current.delete(noteKey);
      } else {
        noteKeySet.current.add(noteKey);
      }
      gridRef.current?.api?.refreshCells({ force: true });
      setUpdateNoteData({});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateNoteData]);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpResults, viewName]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <MpVersionFilter viewName={viewName} mpVersionValue={mpVersionFilter} onChangeFilter={setMpVersionFilter} statusFilters={[PLANNED, RELEASED]} />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} label={"itemHrchy"} styles={{ mr: 1 }} enableSelectParent />
        <ItemTypeFilter viewName={viewName} itemTypeValue={itemTypeFilter} onChangeFilter={setItemTypeFilter} itemTypeFilters={[ITEM_TYPE.KIT, ITEM_TYPE.FG, ITEM_TYPE.SFG]}/>
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <ZeroExclusionFilter exceptionList={EXCEPTION_LIST} appliedExceptions={appliedExceptions} setAppliedExceptions={setAppliedExceptions} />
        <FormControlLabel
          control={<Checkbox color="primary" name={"childComponent"} checked={isChildComp} onChange={handleCheckbox} />}
          label={t("childComponent")}
          key="childComponent"
          sx={{ display: "inline-flex", minWidth: 110 }}
        />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
          <Tooltip title={t("notes")}>
            <IconButton size="small" color="info" onClick={handleNoteModal}>
              <EditNote />
            </IconButton>
          </Tooltip>
          <ExcelExportButton title={title} service={mpReviewService} setExcelData={setGridDatas} columnDefs={[...defaultExportColumnDefs, ...bucketColumnDefs.current]}
            totalRows={resultTotalCount.current * rowsPerCombination} params={{ ...getRequestParams(false), measureCount: rowsPerCombination }} />
        </Box>
      </FilterContainer>
      <Box className={"grid-area"} sx={{ display: "flex" }}>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
        <SplitterContainer>
          <Box sx={{ height: "100%" }}>
            <Box id="mp-review-grid" className="ag-theme-balham grid-area non-background-selected-row" mb={1} sx={{ height: "calc(100% - 50px)" }}>
              <AgGridReact
                ref={gridRef}
                rowData={mpResults}
                {...defaultGridMemo}
                rowSelection={{
                  ...defaultGridMemo.rowSelection,
                  groupSelects: "descendants",
                }}
                getRowStyle={getRowStyle}
                onGridReady={onGridReady}
                onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
                onRowSelected={onRowSelected}
                onCellClicked={onCellGridClicked}
                statusBar={statusBar}
                groupDisplayType={'custom'}
                groupDefaultExpanded={-1}
                groupHideOpenParents
                onRowDataUpdated={initCheck}
              >
              </AgGridReact>
            </Box>
            <PaginationContainer
              currentPage={currentPage} totalPages={gridPages}
              pageSize={pageSize} pageSizes={pageSizes}
              setCurrentPage={setCurrentPage} setPageSize={setPageSize}
            />
          </Box>
          <Box id="mp-review-chart" className="chart-grid-area chart-area" sx={{ height: "100%" }}>
            <PivotChartComposed
              pivotConfig={pivotConfig}
              data={chartData}
              options={{ tooltipContentStyle: { fontSize: FS.SUB } }}
              nullConvert
              activeMeasures={measures.map(measure => measureKeyMap[measure] || measure)}
            />
          </Box>
        </SplitterContainer>
        </Box>
        {!noteOpen ? (
          <NotesModal
            version={mpVersionFilter}
            timeDimension={buckets.WEEK}
            cellData={noteCellData}
            pageCd={viewName}
            setUpdateCellData={setUpdateNoteData}
            close={handleNoteModal}
          />
        ) : null}
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasure}
        applyMeasures={applyMeasures}
        measureKeyMap={measureKeyMap}
      />
      <DimensAttrModal
        open={dimenModalOpen}
        close={handleDimenClose}
        viewName={`${viewName}_ETC`}
        dimensionAttrData={dimensionAttrData}
        setDimensionAttrData={setDimensionAttrData}
        selectLv={{}}
        attrConfig={attrConfig}
      />
    </>
  );
};

export default withTranslation()(MpReview);
